import React, { useState } from 'react';
import { Download, FileSpreadsheet } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AdminExport = ({ token }) => {
  const [loading, setLoading] = useState(false);

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Get headers from first object
    const headers = Object.keys(data[0]);
    
    // Create CSV content
    const csvContent = [
      headers.join(','), // Header row
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          // Handle arrays and objects
          if (Array.isArray(value)) {
            return `"${value.join('; ')}"`;
          }
          if (typeof value === 'object' && value !== null) {
            return `"${JSON.stringify(value)}"`;
          }
          // Escape quotes and wrap in quotes if contains comma
          const stringValue = String(value || '');
          if (stringValue.includes(',') || stringValue.includes('"')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        }).join(',')
      )
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success(`${filename} exported successfully`);
  };

  const handleExportProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/products?status=`);
      const products = response.data.map(p => ({
        ID: p.id,
        Name: p.name,
        Brand: p.brand,
        Price: p.price,
        Discount: p.discount,
        Stock: p.stock_quantity,
        Rating: p.rating || 'N/A',
        Reviews: p.reviews_count || 0,
        Status: p.status
      }));
      exportToCSV(products, 'products');
    } catch (error) {
      toast.error('Failed to export products');
    } finally {
      setLoading(false);
    }
  };

  const handleExportOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/orders`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` }
      });
      const orders = response.data.map(o => ({
        'Order ID': o.id,
        'Date': new Date(o.created_at).toLocaleDateString(),
        'Customer Phone': o.phone,
        'City': o.city,
        'Total Amount': o.total_amount,
        'Status': o.status,
        'Items Count': o.items.length
      }));
      exportToCSV(orders, 'orders');
    } catch (error) {
      toast.error('Failed to export orders');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/categories`);
      const categories = response.data.map(c => ({
        ID: c.id,
        Name: c.name,
        Slug: c.slug,
        Description: c.description || 'N/A'
      }));
      exportToCSV(categories, 'categories');
    } catch (error) {
      toast.error('Failed to export categories');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="font-serif text-3xl font-bold">Export Data</h2>
      
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Products
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Export all products with pricing, stock, and ratings
            </p>
            <Button 
              className="w-full" 
              onClick={handleExportProducts}
              disabled={loading}
            >
              <Download className="w-4 h-4 mr-2" />
              Export Products
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Orders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Export all orders with customer and payment details
            </p>
            <Button 
              className="w-full" 
              onClick={handleExportOrders}
              disabled={loading}
            >
              <Download className="w-4 h-4 mr-2" />
              Export Orders
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Categories
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Export all categories and their details
            </p>
            <Button 
              className="w-full" 
              onClick={handleExportCategories}
              disabled={loading}
            >
              <Download className="w-4 h-4 mr-2" />
              Export Categories
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};